import { useEffect, useRef, useState } from 'react';
import { Store, ChevronDown, CheckCircle } from 'lucide-react';
import type { MerchantMembership } from '../../../api/types';

interface StoreSwitcherProps {
  memberships: MerchantMembership[];
  currentId: string;
  onPick: (id: string) => void;
}

/**
 * Merchant topbar store-switcher.
 *
 * - 0 memberships (admin-via-URL): renders a minimal "Minha loja" label.
 * - 1 membership: renders the store name as a static label (no dropdown).
 * - >1 memberships: renders a button with chevron; clicking opens a dropdown
 *   listing all stores with a check-circle on the active one. Clicking outside
 *   closes the dropdown (mousedown listener + ref).
 *
 * Theme: `--m-*` tokens only (no `--ff-*` or dark-admin classes).
 */
export function StoreSwitcher({ memberships, currentId, onPick }: StoreSwitcherProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const current = memberships.find((m) => m.id === currentId);
  const displayName = current?.name ?? 'Minha loja';

  // No memberships (admin browsing by URL): render a minimal label.
  if (memberships.length === 0) {
    return (
      <div className="flex items-center gap-2 px-2 py-1.5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--m-r-md)] bg-[var(--m-bg-alt)] text-[var(--m-brand)]">
          <Store size={16} />
        </span>
        <span className="text-sm font-semibold text-[var(--m-fg)]">{displayName}</span>
      </div>
    );
  }

  // Single membership: static label, no interaction.
  if (memberships.length === 1) {
    return (
      <div className="flex items-center gap-2 px-2 py-1.5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--m-r-md)] bg-[var(--m-bg-alt)] text-[var(--m-brand)]">
          <Store size={16} />
        </span>
        <span className="text-sm font-semibold text-[var(--m-fg)]">{displayName}</span>
      </div>
    );
  }

  // Multiple memberships: dropdown.
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-[var(--m-r-md)] px-2 py-1.5 hover:bg-[var(--m-surface-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--m-brand)]"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--m-r-md)] bg-[var(--m-bg-alt)] text-[var(--m-brand)]">
          <Store size={16} />
        </span>
        <span className="text-sm font-semibold text-[var(--m-fg)]">{displayName}</span>
        <ChevronDown
          size={14}
          className={`shrink-0 text-[var(--m-fg-muted)] transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Trocar de loja"
          className="absolute left-0 top-full z-50 mt-1 min-w-[220px] rounded-[var(--m-r-md)] border border-[var(--m-divider)] bg-[var(--m-surface)] shadow-[var(--m-shadow-md)]"
        >
          <p className="px-3 pb-1.5 pt-2.5 text-[10px] font-semibold uppercase tracking-widest text-[var(--m-fg-subtle)]">
            Trocar de loja
          </p>
          <ul className="pb-1.5">
            {memberships.map((m) => {
              const isActive = m.id === currentId;
              return (
                <li key={m.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    onClick={() => {
                      onPick(m.id);
                      setOpen(false);
                    }}
                    className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors hover:bg-[var(--m-surface-muted)] ${
                      isActive
                        ? 'font-semibold text-[var(--m-brand)]'
                        : 'text-[var(--m-fg)]'
                    }`}
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--m-r-md)] bg-[var(--m-bg-alt)] text-[var(--m-fg-muted)]">
                      <Store size={14} />
                    </span>
                    <span className="flex-1 truncate font-medium">{m.name}</span>
                    {isActive && (
                      <CheckCircle size={14} className="shrink-0 text-[var(--m-brand)]" aria-hidden="true" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
